import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Box, useTheme} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import {compose} from "redux";
import {NODE_TYPE_CKAN, NODE_TYPE_DATAPORTAL, NODE_TYPE_MERGE} from "../../../../constants/nodes.js";
import {NodeDataAccessibility, NodeVisibility} from "../../../../model/IHubMinimalNode.d.ts";
import MaterialTable from "../../../material-table";
import {
  clearNodePermissionsConfig,
  fetchNodePermissionsConfig,
  sendNodePermissionsConfig
} from "../../../../state/node-permissions-config/nodePermissionsConfigActions.js";
import {
  PERMISSIONS_KEY_APPROVE_PACKAGE,
  PERMISSIONS_KEY_IMPORT_DATA,
  PERMISSIONS_KEY_MANAGE_CACHE,
  PERMISSIONS_KEY_MANAGE_CONFIG,
  PERMISSIONS_KEY_MANAGE_TEMPLATE,
  PERMISSIONS_KEY_VIEW_NODE
} from "../../../../utils/permissions.js";

const getPermissions = (visible, dataAccess, additionalPermissions = [], t) => {
  const permissions = [];

  if (visible === NodeVisibility.Profiled && dataAccess === NodeDataAccessibility.Profiled) {
    permissions.push({
      id: PERMISSIONS_KEY_VIEW_NODE,
      label: t ? t("scenes.nodeSettings.permissions.table.columns.viewNode") : PERMISSIONS_KEY_VIEW_NODE
    });
  }

  permissions.push(
    {
      id: PERMISSIONS_KEY_MANAGE_CACHE,
      label: t ? t("scenes.nodeSettings.permissions.table.columns.manageCache") : PERMISSIONS_KEY_MANAGE_CACHE,
      excludeNodeTypes: [NODE_TYPE_MERGE]
    },
    {
      id: PERMISSIONS_KEY_MANAGE_TEMPLATE,
      label: t ? t("scenes.nodeSettings.permissions.table.columns.manageTemplate") : PERMISSIONS_KEY_MANAGE_TEMPLATE
    },
    {
      id: PERMISSIONS_KEY_MANAGE_CONFIG,
      label: t ? t("scenes.nodeSettings.permissions.table.columns.manageConfig") : PERMISSIONS_KEY_MANAGE_CONFIG
    },
    {
      id: PERMISSIONS_KEY_IMPORT_DATA,
      label: t ? t("modules.packageManager.permissions.importData") : PERMISSIONS_KEY_IMPORT_DATA,
      includeNodeTypes: [NODE_TYPE_CKAN, NODE_TYPE_DATAPORTAL]
    },
    {
      id: PERMISSIONS_KEY_APPROVE_PACKAGE,
      label: t ? t("modules.packageManager.permissions.approvePackage") : PERMISSIONS_KEY_APPROVE_PACKAGE
    }
  );

  return permissions.concat(additionalPermissions);
};

// Filter function for permissions based on includeNodeTypes/excludeNodeTypes
const getFilterPermissionsByNodeType = (permissions, nodeType) =>
  permissions.filter(({includeNodeTypes, excludeNodeTypes}) => {
    // If includeNodeTypes is not present, consider included for all
    const included = !includeNodeTypes || includeNodeTypes.includes(nodeType);
    // If excludeNodeTypes is not present, do not exclude anything
    const excluded = excludeNodeTypes && excludeNodeTypes.includes(nodeType);
    return included && !excluded;
  });

const Form = forwardRef(({config, modulesPermissions, nodeId, node, onSubmit, onCancel}, ref) => {
  const {t} = useTranslation();

  const theme = useTheme();

  const tableStyle = {
    "& .infinite-scroll-table__header__cell": {
      color: theme.palette.text.primary
    },
    "& .infinite-scroll-table__grid__cell": {
      color: theme.palette.text.primary
    }
  };

  const {register, handleSubmit, watch, setValue} = useForm({
    defaultValues: {
      config: config?.map((user, index) => {
        const res = {...user};
        getFilterPermissionsByNodeType(
          getPermissions(node.visible, node.dataAccess, modulesPermissions, t),
          node.type
        ).forEach(({id}) => {
          res[id] = config[index].permission.includes(`${id}_SingleNode_${nodeId}`) === true;
        });
        return res;
      })
    }
  });

  useEffect(() => {
    if (config) {
      config.forEach((_, index) =>
        getFilterPermissionsByNodeType(
          getPermissions(node.visible, node.dataAccess, modulesPermissions, t),
          node.type
        ).forEach(({id}) => register(`config[${index}].${id}`))
      );
    }
  }, [register, config, modulesPermissions, node, t]);

  useImperativeHandle(ref, () => ({
    submit(f) {
      handleSubmit(val => {
        const data = val.config.map((userVal, index) => {
          const res = {
            userId: config[index].userId,
            nodePermissions: []
          };
          getFilterPermissionsByNodeType(
            getPermissions(node.visible, node.dataAccess, modulesPermissions, t),
            node.type
          )
            .map(({id}) => id)
            .forEach(p => {
              if (userVal[p] === true) {
                res.nodePermissions.push(p);
              }
            });
          res.tableData = undefined;
          return res;
        });

        onSubmit(nodeId, data);
        f(data);
      })();
    },
    cancel(f) {
      onCancel();
      f();
    }
  }));

  const [parsedConfig, setParsedConfig] = useState([]);
  useEffect(() => {
    setParsedConfig(nodeId === null ? undefined : config.map((row, idx) => ({...row, idx: idx})) || []);
  }, [nodeId, config]);

  const handleCheckBoxChange = (valueKey, value) => {
    setValue(valueKey, value);
    setParsedConfig(nodeId === null ? undefined : config.map((row, idx) => ({...row, idx: idx})) || []);
  };

  return parsedConfig && node ? (
    <Box id="settings-select__node-permissions-form" component="span" sx={tableStyle}>
      <MaterialTable
        data={parsedConfig}
        idKey={"userId"}
        columns={[
          {
            field: "firstName",
            title: t("scenes.nodeSettings.permissions.table.columns.userFirstName"),
            render: ({firstName}) => <div style={{wordBreak: "normal"}}>{firstName}</div>,
            renderText: ({firstName}) => firstName,
            renderTooltip: ({firstName}) => firstName
          },
          {
            field: "lastName",
            title: t("scenes.nodeSettings.permissions.table.columns.userLastName"),
            render: ({lastName}) => <div style={{wordBreak: "normal"}}>{lastName}</div>,
            renderText: ({lastName}) => lastName,
            renderTooltip: ({lastName}) => lastName
          },
          {
            field: "organization",
            title: t("scenes.nodeSettings.permissions.table.columns.userOrganization"),
            render: ({organization}) => <div>{organization}</div>,
            renderText: ({organization}) => organization,
            renderTooltip: ({organization}) => organization
          },
          {
            field: "email",
            title: t("scenes.nodeSettings.permissions.table.columns.userEmail"),
            render: ({email}) => <div style={{wordBreak: "normal"}}>{email}</div>,
            renderText: ({email}) => email,
            renderTooltip: ({email}) => email
          },
          ...getFilterPermissionsByNodeType(
            getPermissions(node.visible, node.dataAccess, modulesPermissions, t),
            node.type
          ).map(({id, label}) => ({
            field: id,
            title: label,
            render: rowData => (
              <Checkbox
                className={`settings-select__node-permissions-form__checkbox${id}`}
                style={{padding: 4}}
                checked={watch(`config[${rowData.idx}].${id}`)}
                onChange={(_, value) => handleCheckBoxChange(`config[${rowData.idx}].${id}`, value)}
              />
            ),
            renderText: () => ""
          }))
        ]}
      />
    </Box>
  ) : null;
});

const mapStateToProps = state => ({
  config: state.nodePermissionsConfig.permissions,
  modulesNodePermissions: state.moduleConfigs.nodePermissions,
  nodes: state.nodesConfig.nodes
});

const mapDispatchToProps = dispatch => ({
  fetchConfig: nodeId => dispatch(fetchNodePermissionsConfig(nodeId)),
  sendConfig: (nodeId, config) => dispatch(sendNodePermissionsConfig(nodeId, config)),
  clearConfig: () => dispatch(clearNodePermissionsConfig())
});

const PermissionsSettingsForm = (
  {config, modulesNodePermissions, nodes, nodeId, fetchConfig, sendConfig, clearConfig},
  ref
) => {
  const [needConfig, setNeedConfig] = useState(nodeId !== null);

  useEffect(() => {
    if (needConfig) {
      setNeedConfig(false);
      fetchConfig(nodeId);
    }
  }, [config, needConfig, setNeedConfig, fetchConfig, nodeId]);

  return (
    (nodeId === null || config) && (
      <Form
        nodeId={nodeId}
        node={nodes.find(node => node.nodeId === nodeId)}
        config={nodeId === null ? undefined : config}
        modulesPermissions={modulesNodePermissions}
        ref={ref}
        onSubmit={sendConfig}
        onCancel={clearConfig}
      />
    )
  );
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true}),
  forwardRef
)(PermissionsSettingsForm);
